import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import {
  clearPosSession,
  getPosExpiration,
  getPosSession,
} from "./api";

export default function POSSessionGuard() {
  const location = useLocation();

  useEffect(() => {
    const segment = location.pathname
      .split("/")
      .filter(Boolean)[0];

    if (!segment) return undefined;

    let company;

    try {
      company = decodeURIComponent(segment);
    } catch {
      return undefined;
    }

    const session = getPosSession(company);

    if (!session?.access_token) {
      return undefined;
    }

    const token = session.access_token;
    const expiresAt = getPosExpiration(company);

    if (!expiresAt) {
      clearPosSession(company, token);

      window.location.replace(
        `/${encodeURIComponent(company)}?session=expired`
      );

      return undefined;
    }

    let timeout;
    let stopped = false;

    const checkExpiration = () => {
      if (stopped) return;

      window.clearTimeout(timeout);

      // Puede haber cambiado la sesión mientras
      // esta instancia del componente seguía montada.
      if (
        getPosSession(company)?.access_token !== token
      ) {
        return;
      }

      const remaining = expiresAt - Date.now();

      if (remaining <= 0) {
        stopped = true;

        clearPosSession(company, token);

        window.location.replace(
          `/${encodeURIComponent(company)}?session=expired`
        );

        return;
      }

      timeout = window.setTimeout(
        checkExpiration,
        Math.min(remaining, 60000)
      );
    };

    const handleVisibility = () => {
      if (
        document.visibilityState === "visible"
      ) {
        checkExpiration();
      }
    };

    checkExpiration();

    window.addEventListener(
      "focus",
      checkExpiration
    );

    window.addEventListener(
      "pageshow",
      checkExpiration
    );

    document.addEventListener(
      "visibilitychange",
      handleVisibility
    );

    return () => {
      stopped = true;

      window.clearTimeout(timeout);

      window.removeEventListener(
        "focus",
        checkExpiration
      );

      window.removeEventListener(
        "pageshow",
        checkExpiration
      );

      document.removeEventListener(
        "visibilitychange",
        handleVisibility
      );
    };
  }, [location.pathname]);

  return null;
}